import React, { useEffect, useState } from 'react'
import FormSection from '../components/FormSection'
import {
  createBidPackage,
  fetchPackageReview,
  fetchScopeFirstReview,
  updateBidPackage
} from '../services/procurementBidsClient'
import {
  filterScopePickerOptions,
  formatScopePickerOptionLabel
} from '../utils/procurementScopePicker'
import '../styles/utilities.css'

function formatDecisionLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .trim() || '--'
}

function formatResponseSummary(summary) {
  if (!summary) {
    return '--'
  }

  return [
    `approved ${summary.approved || 0}`,
    `deferred ${summary.deferred || 0}`,
    `review ${summary.review || 0}`,
    `gaps ${summary.gaps || 0}`
  ].join(' | ')
}

function formatSubmissionStatusCounts(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return '--'
  }

  return items
    .map((item) => `${formatDecisionLabel(item.submission_status)} ${item.count || 0}`)
    .join(' | ')
}

function formatReviewReadinessLabel(value) {
  const normalized = String(value || '').trim()
  if (!normalized) {
    return '--'
  }

  if (normalized === 'send_more_rfps') {
    return 'Send More RFPs'
  }

  if (normalized === 'begin_review') {
    return 'Begin Review'
  }

  if (normalized === 'targets_not_configured') {
    return 'Targets Not Configured'
  }

  return formatDecisionLabel(normalized)
}

function formatPackageOptionLabel(packageOption) {
  if (!packageOption) {
    return '--'
  }

  const parts = [
    packageOption.package_id,
    packageOption.package_name,
    packageOption.community_name,
    packageOption.project_id ? `project: ${packageOption.project_id}` : null,
    packageOption.status ? `status: ${packageOption.status}` : null
  ].filter(Boolean)

  return parts.join(' | ')
}

function formatLinePreview(item) {
  const decisionScopeId = item.review_decision?.scope_id
  const proposalScopeId = item.proposal?.scope_id_candidate
  const selectedScopeId = decisionScopeId || proposalScopeId || 'no scope'
  const status = item.review_decision?.mapping_status || item.review_state || '--'

  return `${item.raw_scope_label || 'Untitled raw scope'} | ${selectedScopeId} | ${formatDecisionLabel(status)}`
}

function createEmptyPackageFormState() {
  return {
    package_id: '',
    project_id: '',
    phase_id: '',
    package_name: '',
    community_name: '',
    product_type: '',
    plan_types_text: '',
    scope_ids: [],
    status: 'draft',
    issued_at: '',
    due_at: '',
    created_by: '',
    notes: '',
    intake_targets_text: '',
    quantity_driver_snapshot_text: ''
  }
}

function parseListInput(value) {
  return String(value || '')
    .split(/[,\n]/)
    .map((entry) => entry.trim())
    .filter(Boolean)
}

function buildPackageFormState(packageItem) {
  return {
    package_id: packageItem?.package_id || '',
    project_id: packageItem?.project_id || '',
    phase_id: packageItem?.phase_id || '',
    package_name: packageItem?.package_name || '',
    community_name: packageItem?.community_name || '',
    product_type: packageItem?.product_type || '',
    plan_types_text: Array.isArray(packageItem?.plan_types) ? packageItem.plan_types.join(', ') : '',
    scope_ids: Array.isArray(packageItem?.scope_ids) ? packageItem.scope_ids : [],
    status: packageItem?.status || 'draft',
    issued_at: packageItem?.issued_at || '',
    due_at: packageItem?.due_at || '',
    created_by: packageItem?.created_by || '',
    notes: packageItem?.notes || '',
    intake_targets_text: packageItem?.intake_targets
      ? JSON.stringify(packageItem.intake_targets, null, 2)
      : '',
    quantity_driver_snapshot_text: packageItem?.quantity_driver_snapshot
      ? JSON.stringify(packageItem.quantity_driver_snapshot, null, 2)
      : ''
  }
}

function buildPackageWritePayload(packageForm) {
  const intakeTargetsText = String(packageForm.intake_targets_text || '').trim()
  const snapshotText = String(packageForm.quantity_driver_snapshot_text || '').trim()
  let intakeTargets = null
  let quantityDriverSnapshot = null

  if (intakeTargetsText) {
    try {
      intakeTargets = JSON.parse(intakeTargetsText)
    } catch (error) {
      throw new Error('Intake targets must be valid JSON')
    }
  }

  if (snapshotText) {
    try {
      quantityDriverSnapshot = JSON.parse(snapshotText)
    } catch (error) {
      throw new Error('Quantity driver snapshot must be valid JSON')
    }
  }

  return {
    package_id: String(packageForm.package_id || '').trim(),
    project_id: String(packageForm.project_id || '').trim(),
    phase_id: String(packageForm.phase_id || '').trim(),
    package_name: String(packageForm.package_name || '').trim(),
    community_name: String(packageForm.community_name || '').trim(),
    product_type: String(packageForm.product_type || '').trim(),
    plan_types: parseListInput(packageForm.plan_types_text),
    scope_ids: Array.isArray(packageForm.scope_ids) ? packageForm.scope_ids : [],
    status: String(packageForm.status || '').trim() || 'draft',
    issued_at: String(packageForm.issued_at || '').trim(),
    due_at: String(packageForm.due_at || '').trim(),
    created_by: String(packageForm.created_by || '').trim(),
    notes: String(packageForm.notes || '').trim(),
    intake_targets: intakeTargets,
    quantity_driver_snapshot: quantityDriverSnapshot
  }
}

const ProcurementReview = ({ onNavigate }) => {
  const [packageIdInput, setPackageIdInput] = useState('')
  const [packageData, setPackageData] = useState(null)
  const [packageLoading, setPackageLoading] = useState(false)
  const [packageError, setPackageError] = useState('')
  const [packageFormMode, setPackageFormMode] = useState('create')
  const [packageForm, setPackageForm] = useState(createEmptyPackageFormState)
  const [packageSaveLoading, setPackageSaveLoading] = useState(false)
  const [packageSaveError, setPackageSaveError] = useState('')
  const [packageSaveMessage, setPackageSaveMessage] = useState('')
  const [packageScopeSearch, setPackageScopeSearch] = useState('')
  const [packageSetupExpanded, setPackageSetupExpanded] = useState(false)
  const [scopeIdInput, setScopeIdInput] = useState('')
  const [scopeSearch, setScopeSearch] = useState('')
  const [scopeData, setScopeData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const packageOptions = packageData?.package_options || []
  const selectedPackage = packageData?.selected_package || null
  const packageScopes = packageData?.scopes || []
  const packageItems = packageData?.items || []
  const packageIntakeTally = packageData?.intake_tally || null
  const canonicalScopeOptions = scopeData?.canonical_scope_options || []
  const filteredPackageScopeOptions = filterScopePickerOptions(
    canonicalScopeOptions,
    packageScopeSearch
  )
  const filteredScopeOptions = filterScopePickerOptions(canonicalScopeOptions, scopeSearch)
  const selectedScope = scopeData?.selected_scope || null
  const items = scopeData?.items || []
  const packageEditLocked = Boolean(selectedPackage?.edit_locked)

  async function loadPackageReview(targetPackageId = '') {
    const normalizedPackageId = String(targetPackageId || '').trim()

    setPackageLoading(true)
    setPackageError('')

    try {
      const data = await fetchPackageReview(normalizedPackageId)
      setPackageData(data)
      if (!normalizedPackageId) {
        return
      }

      setPackageIdInput(normalizedPackageId)
    } catch (loadError) {
      console.error('Procurement package review load failed:', loadError)
      setPackageError(loadError.message || 'Failed to load package review surface')
      if (!normalizedPackageId) {
        setPackageData({
          package_options: [],
          selected_package: null,
          response_count: 0,
          scope_count: 0,
          items: [],
          scopes: [],
          intake_tally: null,
          empty_state: null
        })
      }
    } finally {
      setPackageLoading(false)
    }
  }

  async function loadScopeReview(targetScopeId = '') {
    const normalizedScopeId = String(targetScopeId || '').trim()

    setLoading(true)
    setError('')

    try {
      const data = await fetchScopeFirstReview(normalizedScopeId)
      setScopeData(data)
      if (!normalizedScopeId) {
        return
      }

      setScopeIdInput(normalizedScopeId)
    } catch (loadError) {
      console.error('Procurement scope-first review load failed:', loadError)
      setError(loadError.message || 'Failed to load procurement review surface')
      if (!normalizedScopeId) {
        setScopeData({
          canonical_scope_options: [],
          selected_scope: null,
          has_bids: null,
          response_count: 0,
          line_count: 0,
          items: [],
          empty_state: null
        })
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPackageReview()
    loadScopeReview()
  }, [])

  function handlePackageSubmit(event) {
    event.preventDefault()
    const normalizedPackageId = String(packageIdInput || '').trim()
    if (!normalizedPackageId) {
      setPackageError('Select a package_id to load the package / RFP review surface.')
      return
    }

    loadPackageReview(normalizedPackageId)
  }

  function handleSubmit(event) {
    event.preventDefault()
    const normalizedScopeId = String(scopeIdInput || '').trim()
    if (!normalizedScopeId) {
      setError('Select a canonical scope to load the scope-first review surface.')
      return
    }

    loadScopeReview(normalizedScopeId)
  }

  function handleOpenScopeFromPackage(scopeId) {
    const normalizedScopeId = String(scopeId || '').trim()
    if (!normalizedScopeId) {
      return
    }

    setScopeIdInput(normalizedScopeId)
    loadScopeReview(normalizedScopeId)
  }

  function updatePackageFormField(field, value) {
    setPackageForm((current) => ({
      ...current,
      [field]: value
    }))
  }

  function handlePackageScopeSelectionChange(event) {
    const selectedScopeIds = Array.from(event.target.selectedOptions || [])
      .map((option) => option.value)
      .filter(Boolean)

    updatePackageFormField('scope_ids', selectedScopeIds)
  }

  function handleStartNewPackage() {
    setPackageFormMode('create')
    setPackageSaveError('')
    setPackageSaveMessage('')
    setPackageForm(createEmptyPackageFormState())
  }

  function handleLoadPackageIntoForm() {
    if (!selectedPackage) {
      return
    }

    setPackageFormMode('edit')
    setPackageSaveError('')
    setPackageSaveMessage('')
    setPackageForm(buildPackageFormState(selectedPackage))
  }

  async function handlePackageFormSubmit(event) {
    event.preventDefault()
    setPackageSaveError('')
    setPackageSaveMessage('')

    let payload

    try {
      payload = buildPackageWritePayload(packageForm)
    } catch (saveError) {
      setPackageSaveError(saveError.message || 'Failed to prepare package payload')
      return
    }

    setPackageSaveLoading(true)

    try {
      const result = packageFormMode === 'edit'
        ? await updateBidPackage(payload.package_id, payload)
        : await createBidPackage(payload)

      setPackageFormMode('edit')
      setPackageForm(buildPackageFormState(result.package || payload))
      setPackageIdInput(payload.package_id)
      await loadPackageReview(payload.package_id)
      setPackageSaveMessage(
        packageFormMode === 'edit'
          ? `Updated package ${payload.package_id}.`
          : `Created package ${payload.package_id}.`
      )
    } catch (saveError) {
      console.error('Procurement package save failed:', saveError)
      setPackageSaveError(saveError.message || 'Failed to save bid package')
    } finally {
      setPackageSaveLoading(false)
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>Procurement Review</h2>
        <p>
          Load package-level intake status, open scope-first review, and jump into response-level
          alias review tools.
        </p>
      </div>

      <div className="actions procurement-review-actions procurement-review-header-actions">
        <button
          type="button"
          className="btn"
          onClick={() => onNavigate?.('procurement-rfp')}
        >
          Procurement RFP Designer
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => onNavigate?.('procurement-scope-review')}
        >
          Procurement Scope Review
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => onNavigate?.('dashboard')}
        >
          Back to Dashboard
        </button>
      </div>

      <FormSection title="Package Setup (Advanced)">
        <div className="procurement-review-note">
          Use this form for package metadata and intake-target maintenance. Daily review work starts
          in the package loader and scope-first sections below.
        </div>

        <div className="actions procurement-review-actions procurement-review-toggle">
          <button
            type="button"
            className="btn"
            onClick={() => setPackageSetupExpanded((current) => !current)}
          >
            {packageSetupExpanded ? 'Hide Package Setup' : 'Show Package Setup'}
          </button>
        </div>

        {!packageSetupExpanded && (
          <div className="procurement-review-note procurement-review-note--compact">
            Package setup is hidden to keep the landing page review-focused.
          </div>
        )}

        {packageSetupExpanded && (
        <form className="procurement-review-form" onSubmit={handlePackageFormSubmit}>
          <label className="procurement-review-input-group">
            <span className="text-strong">Package ID</span>
            <input
              type="text"
              value={packageForm.package_id}
              onChange={(event) => updatePackageFormField('package_id', event.target.value)}
              placeholder="pkg_2026_001"
              readOnly={packageFormMode === 'edit'}
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Project ID</span>
            <input
              type="text"
              value={packageForm.project_id}
              onChange={(event) => updatePackageFormField('project_id', event.target.value)}
              placeholder="aria169"
              readOnly={packageFormMode === 'edit'}
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Package Name</span>
            <input
              type="text"
              value={packageForm.package_name}
              onChange={(event) => updatePackageFormField('package_name', event.target.value)}
              placeholder="Windows and Storefront Package"
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Phase ID</span>
            <input
              type="text"
              value={packageForm.phase_id}
              onChange={(event) => updatePackageFormField('phase_id', event.target.value)}
              placeholder="phase_1"
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Community Name</span>
            <input
              type="text"
              value={packageForm.community_name}
              onChange={(event) => updatePackageFormField('community_name', event.target.value)}
              placeholder="Aria SF"
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Product Type</span>
            <input
              type="text"
              value={packageForm.product_type}
              onChange={(event) => updatePackageFormField('product_type', event.target.value)}
              placeholder="midrise"
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Plan Types</span>
            <input
              type="text"
              value={packageForm.plan_types_text}
              onChange={(event) => updatePackageFormField('plan_types_text', event.target.value)}
              placeholder="A1, B2"
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Scope / Trade Search</span>
            <input
              type="text"
              value={packageScopeSearch}
              onChange={(event) => setPackageScopeSearch(event.target.value)}
              placeholder="Search by trade/common name, canonical scope, or id"
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Canonical Scopes</span>
            <select
              multiple
              value={packageForm.scope_ids}
              onChange={handlePackageScopeSelectionChange}
              size={Math.min(Math.max(filteredPackageScopeOptions.length || 4, 4), 10)}
            >
              {filteredPackageScopeOptions.map((scopeOption) => (
                <option key={scopeOption.scope_id} value={scopeOption.scope_id}>
                  {formatScopePickerOptionLabel(scopeOption)}
                </option>
              ))}
            </select>
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Status</span>
            <input
              type="text"
              value={packageForm.status}
              onChange={(event) => updatePackageFormField('status', event.target.value)}
              placeholder="draft"
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Issued At</span>
            <input
              type="text"
              value={packageForm.issued_at}
              onChange={(event) => updatePackageFormField('issued_at', event.target.value)}
              placeholder="2026-03-23T00:00:00Z"
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Due At</span>
            <input
              type="text"
              value={packageForm.due_at}
              onChange={(event) => updatePackageFormField('due_at', event.target.value)}
              placeholder="2026-04-06T00:00:00Z"
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Created By</span>
            <input
              type="text"
              value={packageForm.created_by}
              onChange={(event) => updatePackageFormField('created_by', event.target.value)}
              placeholder="procurement_reviewer"
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Notes</span>
            <textarea
              rows="4"
              value={packageForm.notes}
              onChange={(event) => updatePackageFormField('notes', event.target.value)}
              placeholder="Bounded package notes"
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Quantity Driver Snapshot JSON</span>
            <textarea
              rows="12"
              value={packageForm.quantity_driver_snapshot_text}
              onChange={(event) => updatePackageFormField('quantity_driver_snapshot_text', event.target.value)}
              placeholder={'{\n  "snapshot_version": 1,\n  "snapshot_status": "partial",\n  "scope_profile_resolutions": [],\n  "drivers": {}\n}'}
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Intake Targets JSON</span>
            <textarea
              rows="8"
              value={packageForm.intake_targets_text}
              onChange={(event) => updatePackageFormField('intake_targets_text', event.target.value)}
              placeholder={'{\n  "requested_vendor_ids": ["sansei-gardens", "green-valley-landscaping", "al-fresco-landscaping"],\n  "minimum_vendor_bids_per_scope": 2\n}'}
            />
          </label>

          <div className="procurement-review-note">
            Quantity driver snapshot JSON is optional. Keep it valid and package-scoped.
          </div>

          <div className="procurement-review-note">
            Intake targets JSON is optional. Use `requested_vendor_ids[]` and
            `minimum_vendor_bids_per_scope`.
          </div>

          {packageSaveError && (
            <div className="banner warn">
              <p>{packageSaveError}</p>
            </div>
          )}

          {packageSaveMessage && (
            <div className="banner success">
              <p>{packageSaveMessage}</p>
            </div>
          )}

          {packageFormMode === 'edit' && packageEditLocked && (
            <div className="procurement-review-note">
              This loaded package is read-only for light edits because bid responses already exist.
              The write bridge in this slice does not introduce package revisioning.
            </div>
          )}

          <div className="actions procurement-review-actions">
            <button
              type="button"
              className="btn"
              onClick={handleStartNewPackage}
            >
              New Package Draft
            </button>
            <button
              type="button"
              className="btn"
              onClick={handleLoadPackageIntoForm}
              disabled={!selectedPackage}
            >
              Edit Loaded Package
            </button>
            <button
              type="submit"
              className="btn primary"
              disabled={packageSaveLoading || (packageFormMode === 'edit' && packageEditLocked)}
            >
              {packageSaveLoading
                ? 'Saving Package...'
                : packageFormMode === 'edit'
                  ? 'Update Package'
                  : 'Create Package'}
            </button>
          </div>
        </form>
        )}
      </FormSection>

      <FormSection title="Package / RFP Loader">
        <form className="procurement-review-form" onSubmit={handlePackageSubmit}>
          <label className="procurement-review-input-group">
            <span className="text-strong">Package ID</span>
            <input
              type="text"
              list="procurement-module-package-options"
              value={packageIdInput}
              onChange={(event) => setPackageIdInput(event.target.value)}
              placeholder="pkg_2026_001"
            />
          </label>
          <div className="actions procurement-review-actions">
            <button
              type="button"
              className="btn"
              onClick={() => onNavigate?.('dashboard')}
            >
              Back to Dashboard
            </button>
            <button
              type="submit"
              className="btn primary"
              disabled={packageLoading || !packageIdInput.trim()}
            >
              {packageLoading ? 'Loading Package...' : 'Load Package Review'}
            </button>
          </div>
        </form>

        {packageOptions.length > 0 && (
          <datalist id="procurement-module-package-options">
            {packageOptions.map((packageOption) => (
              <option key={packageOption.package_id} value={packageOption.package_id}>
                {formatPackageOptionLabel(packageOption)}
              </option>
            ))}
          </datalist>
        )}

        <div className="procurement-review-note">
          Load an existing package to review readiness, intake coverage, and received responses.
        </div>

        {packageError && (
          <div className="banner warn">
            <p>{packageError}</p>
          </div>
        )}
      </FormSection>

      {selectedPackage && (
        <FormSection title="Package Availability">
          <div className="procurement-review-summary-grid">
            <div className="procurement-review-stat">
              <span className="procurement-review-stat-label">Package</span>
              <span className="procurement-review-stat-value">{selectedPackage.package_id}</span>
            </div>
            <div className="procurement-review-stat">
              <span className="procurement-review-stat-label">Responses</span>
              <span className="procurement-review-stat-value">{packageData?.response_count || 0}</span>
            </div>
            <div className="procurement-review-stat">
              <span className="procurement-review-stat-label">Scopes</span>
              <span className="procurement-review-stat-value">{packageData?.scope_count || 0}</span>
            </div>
            <div className="procurement-review-stat">
              <span className="procurement-review-stat-label">Status</span>
              <span className="procurement-review-stat-value">{selectedPackage.status || '--'}</span>
            </div>
          </div>

          <div className="procurement-review-note">
            {formatPackageOptionLabel(selectedPackage)}
            {selectedPackage.product_type ? ` | product: ${selectedPackage.product_type}` : ''}
            {selectedPackage.phase_id ? ` | phase: ${selectedPackage.phase_id}` : ''}
            {selectedPackage.intake_targets_present
              ? ' | intake targets present'
              : ' | intake targets not configured'}
            {selectedPackage.quantity_driver_snapshot_present
              ? ' | quantity snapshot present'
              : ' | quantity snapshot not loaded here'}
          </div>

          {packageIntakeTally && (
            <>
              <div className="procurement-review-note">
                Header-level intake tally from response registrations. Use this to decide whether to
                send more RFPs or begin review.
              </div>

              <div className="procurement-review-summary-grid">
                <div className="procurement-review-stat">
                  <span className="procurement-review-stat-label">Review Readiness</span>
                  <span className="procurement-review-stat-value">
                    {formatReviewReadinessLabel(packageIntakeTally.review_readiness?.status)}
                  </span>
                </div>
                <div className="procurement-review-stat">
                  <span className="procurement-review-stat-label">Vendors Responded</span>
                  <span className="procurement-review-stat-value">{packageIntakeTally.responded_vendor_count || 0}</span>
                </div>
                <div className="procurement-review-stat">
                  <span className="procurement-review-stat-label">Requested Vendors</span>
                  <span className="procurement-review-stat-value">
                    {packageIntakeTally.responded_requested_vendor_count || 0} / {packageIntakeTally.requested_vendor_count || 0}
                  </span>
                </div>
                <div className="procurement-review-stat">
                  <span className="procurement-review-stat-label">Scope Responses</span>
                  <span className="procurement-review-stat-value">{packageIntakeTally.scope_response_total || 0}</span>
                </div>
                <div className="procurement-review-stat">
                  <span className="procurement-review-stat-label">Covered Scopes</span>
                  <span className="procurement-review-stat-value">{packageIntakeTally.covered_scope_count || 0} / {packageIntakeTally.requested_scope_count || 0}</span>
                </div>
                <div className="procurement-review-stat">
                  <span className="procurement-review-stat-label">Artifacts</span>
                  <span className="procurement-review-stat-value">{packageIntakeTally.total_artifact_count || 0}</span>
                </div>
              </div>

              <div className="procurement-review-leveling-cta">
                <button
                  type="button"
                  className="btn primary procurement-review-leveling-btn"
                  onClick={() => onNavigate?.('procurement-leveling', selectedPackage.package_id)}
                >
                  Begin Bid Leveling
                </button>
              </div>

              {packageIntakeTally.review_readiness?.minimum_vendor_bids_per_scope ? (
                <div className="procurement-review-note">
                  Minimum vendor bids per requested scope:{' '}
                  {packageIntakeTally.review_readiness.minimum_vendor_bids_per_scope}. Pending
                  requested vendors: {packageIntakeTally.review_readiness.pending_vendor_count || 0}
                  {packageIntakeTally.review_readiness.pending_vendor_ids?.length
                    ? ` | ${packageIntakeTally.review_readiness.pending_vendor_ids.join(', ')}`
                    : ''}
                </div>
              ) : (
                <div className="procurement-review-note">
                  Review-readiness targets are not configured on this package yet. Counts still
                  read back correctly from intake headers.
                </div>
              )}

              {packageIntakeTally.review_readiness?.unmet_scopes?.length > 0 && (
                <div className="procurement-review-note">
                  Scope gaps still blocking begin-review:{' '}
                  {packageIntakeTally.review_readiness.unmet_scopes
                    .map((scopeItem) => `${scopeItem.scope_id} ${scopeItem.responded_vendor_count}/${scopeItem.minimum_vendor_bids_required}`)
                    .join(' | ')}
                </div>
              )}

              <div className="procurement-review-note">
                Submission status tally: {formatSubmissionStatusCounts(packageIntakeTally.submission_status_counts)}
              </div>
            </>
          )}

          {packageScopes.length > 0 && (
            <div className="procurement-review-package-scope-list">
              {packageScopes.map((scopeItem) => (
                <div
                  key={scopeItem.scope_id}
                  className="procurement-review-package-scope-card"
                >
                  <div className="procurement-review-cell-title">
                    {scopeItem.scope_name || scopeItem.scope_id}
                  </div>
                  <div className="procurement-review-cell-meta">
                    {formatScopePickerOptionLabel(scopeItem)}
                  </div>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => handleOpenScopeFromPackage(scopeItem.scope_id)}
                  >
                    Open Scope-First Review
                  </button>
                </div>
              ))}
            </div>
          )}

          {packageIntakeTally?.scope_receipt_counts?.length > 0 && (
            <>
              <div className="procurement-review-note">
                <strong>Scope coverage:</strong> use this tally to see which requested scopes have
                enough returns to review and which scopes may still need more outreach.
              </div>
              <div className="procurement-review-package-scope-list">
                {packageIntakeTally.scope_receipt_counts.map((scopeItem) => (
                  <div
                    key={scopeItem.scope_id || scopeItem.scope_name}
                    className="procurement-review-package-scope-card"
                  >
                    <div className="procurement-review-cell-title">
                      {scopeItem.scope_name || scopeItem.scope_id || 'Unknown scope'}
                    </div>
                    <div className="procurement-review-cell-meta">
                      {scopeItem.scope_id || '--'}
                      {scopeItem.scope_family ? ` | ${scopeItem.scope_family}` : ''}
                    </div>
                    <div className="procurement-review-cell-meta">
                      {scopeItem.response_count || 0} response{scopeItem.response_count === 1 ? '' : 's'}
                      {' | '}
                      {scopeItem.responded_vendor_count || 0} vendor{scopeItem.responded_vendor_count === 1 ? '' : 's'}
                    </div>
                    <div className="procurement-review-cell-meta">
                      {scopeItem.ready_for_review === null
                        ? 'Review target: not configured'
                        : `${scopeItem.ready_for_review ? 'Ready to review' : 'Needs more RFPs'} | ${scopeItem.responded_vendor_count || 0}/${scopeItem.minimum_vendor_bids_required || 0} vendor bids`}
                    </div>
                    <div className="procurement-review-cell-meta">
                      {formatSubmissionStatusCounts(scopeItem.submission_status_counts)}
                    </div>
                    <div className="procurement-review-cell-meta">
                      {scopeItem.latest_submitted_at ? `Latest receipt: ${scopeItem.latest_submitted_at}` : 'Latest receipt: --'}
                    </div>
                    <button
                      type="button"
                      className="btn"
                      onClick={() => handleOpenScopeFromPackage(scopeItem.scope_id)}
                      disabled={!scopeItem.scope_id}
                    >
                      Open Scope-First Review
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {packageIntakeTally?.responded_vendors?.length > 0 && (
            <>
              <div className="procurement-review-note">
                <strong>Who responded:</strong> package-linked vendor receipts are visible here
                before detailed extraction starts.
              </div>
              <div className="procurement-review-response-list">
                {packageIntakeTally.responded_vendors.map((vendorItem) => (
                  <div
                    key={vendorItem.vendor_id || vendorItem.vendor_name}
                    className="procurement-review-response-card"
                  >
                    <div className="procurement-review-response-grid">
                      <div>
                        <div className="procurement-review-cell-title">
                          {vendorItem.vendor_name || vendorItem.vendor_id || 'Unknown vendor'}
                        </div>
                        <div className="procurement-review-cell-meta">
                          {vendorItem.vendor_id || '--'}
                        </div>
                      </div>
                      <div>
                        <div className="procurement-review-cell-title">
                          {vendorItem.response_count || 0} response{vendorItem.response_count === 1 ? '' : 's'}
                        </div>
                        <div className="procurement-review-cell-meta">
                          {vendorItem.artifact_count || 0} artifact{vendorItem.artifact_count === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div>
                        <div className="procurement-review-cell-title">
                          {vendorItem.latest_submitted_at || 'submitted_at unavailable'}
                        </div>
                        <div className="procurement-review-cell-meta">
                          {formatSubmissionStatusCounts(vendorItem.submission_status_counts)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {!packageData?.response_count ? (
            <div className="procurement-review-empty">
              {packageData?.empty_state?.message || 'No bid responses are registered for this package yet.'}
            </div>
          ) : (
            <div className="procurement-review-response-list">
              {packageItems.map((responseItem) => (
                <div key={responseItem.response_id} className="procurement-review-response-card">
                  <div className="procurement-review-response-grid">
                    <div>
                      <div className="procurement-review-cell-title">
                        {responseItem.response_id || 'Unknown response'}
                      </div>
                      <div className="procurement-review-cell-meta">
                        {responseItem.submission_status || 'status unavailable'}
                      </div>
                      <div className="procurement-review-cell-meta">
                        {responseItem.submitted_at || 'submitted_at unavailable'}
                      </div>
                    </div>
                    <div>
                      <div className="procurement-review-cell-title">
                        {responseItem.vendor_name || responseItem.vendor_id || 'Unknown vendor'}
                      </div>
                      <div className="procurement-review-cell-meta">
                        {responseItem.vendor_id || '--'}
                        {responseItem.vendor_trade ? ` | ${responseItem.vendor_trade}` : ''}
                      </div>
                    </div>
                    <div>
                      <div className="procurement-review-cell-title">
                        {selectedPackage.package_name || responseItem.package_name || responseItem.package_id || 'Unknown package'}
                      </div>
                      <div className="procurement-review-cell-meta">
                        {responseItem.package_id || '--'}
                        {selectedPackage.community_name ? ` | ${selectedPackage.community_name}` : ''}
                      </div>
                    </div>
                    <div>
                      <div className="procurement-review-cell-title">
                        {responseItem.total_raw_line_count} raw line{responseItem.total_raw_line_count === 1 ? '' : 's'}
                      </div>
                      <div className="procurement-review-cell-meta">
                        {responseItem.artifact_count} artifact{responseItem.artifact_count === 1 ? '' : 's'}
                      </div>
                      {responseItem.artifacts && responseItem.artifacts.length > 0 && (
                        <div className="procurement-review-artifacts-list">
                          {responseItem.artifacts.map((a, i) => (
                            <div key={i} className="procurement-review-cell-meta procurement-review-artifact-item">
                              &bull; <strong>{a.role}</strong>: {a.file_name || a.key.split('/').pop()}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="procurement-review-cell-meta">
                        {responseItem.scope_ids?.length
                          ? `Scopes: ${responseItem.scope_ids.join(', ')}`
                          : 'Scopes: package fallback'}
                      </div>
                      <div className="procurement-review-cell-meta">
                        {responseItem.template_family || 'template family unavailable'}
                        {responseItem.sbf_artifact_id ? ` | ${responseItem.sbf_artifact_id}` : ''}
                        {responseItem.portal_submission_id ? ` | ${responseItem.portal_submission_id}` : ''}
                      </div>
                    </div>
                  </div>

                  <div className="procurement-review-actions">
                    <button
                      type="button"
                      className="btn primary"
                      onClick={() => onNavigate?.('procurement-scope-review', responseItem.response_id)}
                    >
                      Open Scope Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </FormSection>
      )}

      <FormSection title="Scope-First Review">
        <form className="procurement-review-form" onSubmit={handleSubmit}>
          <label className="procurement-review-input-group">
            <span className="text-strong">Scope / Trade Search</span>
            <input
              type="text"
              value={scopeSearch}
              onChange={(event) => setScopeSearch(event.target.value)}
              placeholder="Search by trade/common name, canonical scope, or id"
            />
          </label>

          <label className="procurement-review-input-group">
            <span className="text-strong">Canonical Scope</span>
            <select
              value={scopeIdInput}
              onChange={(event) => setScopeIdInput(event.target.value)}
            >
              <option value="">Select a canonical scope</option>
              {filteredScopeOptions.map((scopeOption) => (
                <option key={scopeOption.scope_id} value={scopeOption.scope_id}>
                  {formatScopePickerOptionLabel(scopeOption)}
                </option>
              ))}
            </select>
          </label>
          <div className="actions procurement-review-actions">
            <button
              type="button"
              className="btn"
              onClick={() => onNavigate?.('dashboard')}
            >
              Back to Dashboard
            </button>
            <button
              type="submit"
              className="btn primary"
              disabled={loading || !scopeIdInput.trim()}
            >
              {loading ? 'Loading Scope...' : 'Load Scope Review'}
            </button>
          </div>
        </form>

        <div className="procurement-review-note">
          Search matches common trade names, trade tags, canonical scope names, family, CSI, and
          canonical id. Example: `landscape`, `landscapers`, `pads`, or `slabs` can surface the
          correct canonical scope. Alias approve/defer writeback still lives in Procurement Scope
          Review.
        </div>

        {error && (
          <div className="banner warn">
            <p>{error}</p>
          </div>
        )}
      </FormSection>

      {selectedScope && (
        <FormSection title="Scope Availability">
          <div className="procurement-review-summary-grid">
            <div className="procurement-review-stat">
              <span className="procurement-review-stat-label">Scope</span>
              <span className="procurement-review-stat-value">{selectedScope.scope_id}</span>
            </div>
            <div className="procurement-review-stat">
              <span className="procurement-review-stat-label">Responses</span>
              <span className="procurement-review-stat-value">{scopeData?.response_count || 0}</span>
            </div>
            <div className="procurement-review-stat">
              <span className="procurement-review-stat-label">Scope Lines</span>
              <span className="procurement-review-stat-value">{scopeData?.line_count || 0}</span>
            </div>
            <div className="procurement-review-stat">
              <span className="procurement-review-stat-label">Family</span>
              <span className="procurement-review-stat-value">{selectedScope.scope_family || '--'}</span>
            </div>
          </div>

          <div className="procurement-review-note">
            {formatScopePickerOptionLabel(selectedScope)}
          </div>

          {!scopeData?.has_bids ? (
            <div className="procurement-review-empty">
              {scopeData?.empty_state?.message || 'No bids present yet for this scope.'}
            </div>
          ) : (
            <div className="procurement-review-response-list">
              {items.map((responseItem) => (
                <div key={responseItem.response_id} className="procurement-review-response-card">
                  <div className="procurement-review-response-grid">
                    <div>
                      <div className="procurement-review-cell-title">
                        {responseItem.response_id || 'Unknown response'}
                      </div>
                      <div className="procurement-review-cell-meta">
                        {responseItem.submission_status || 'status unavailable'}
                      </div>
                      <div className="procurement-review-cell-meta">
                        {responseItem.submitted_at || 'submitted_at unavailable'}
                      </div>
                    </div>
                    <div>
                      <div className="procurement-review-cell-title">
                        {responseItem.vendor_name || responseItem.vendor_id || 'Unknown vendor'}
                      </div>
                      <div className="procurement-review-cell-meta">
                        {responseItem.vendor_id || '--'}
                        {responseItem.vendor_trade ? ` | ${responseItem.vendor_trade}` : ''}
                      </div>
                    </div>
                    <div>
                      <div className="procurement-review-cell-title">
                        {responseItem.package_name || responseItem.package_id || 'Unknown package'}
                      </div>
                      <div className="procurement-review-cell-meta">
                        {responseItem.package_id || '--'}
                      </div>
                    </div>
                    <div>
                      <div className="procurement-review-cell-title">
                        {responseItem.scope_line_count} scope line{responseItem.scope_line_count === 1 ? '' : 's'}
                      </div>
                      <div className="procurement-review-cell-meta">
                        {responseItem.total_raw_line_count} total raw line{responseItem.total_raw_line_count === 1 ? '' : 's'}
                      </div>
                      <div className="procurement-review-cell-meta">
                        {formatResponseSummary(responseItem.summary)}
                      </div>
                    </div>
                  </div>

                  {responseItem.items.length > 0 ? (
                    <div className="procurement-review-response-lines">
                      {responseItem.items.slice(0, 3).map((lineItem) => (
                        <div key={lineItem.line_id} className="procurement-review-line-preview">
                          <div className="procurement-review-cell-title">
                            {lineItem.line_id} | {lineItem.raw_description || 'No description provided'}
                          </div>
                          <div className="procurement-review-cell-meta">
                            {formatLinePreview(lineItem)}
                          </div>
                        </div>
                      ))}
                      {responseItem.items.length > 3 && (
                        <div className="procurement-review-cell-meta">
                          {responseItem.items.length - 3} more scope line{responseItem.items.length - 3 === 1 ? '' : 's'} available in the full review.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="procurement-review-note">
                      Response exists in this scope context, but no current raw lines are mapped or proposed to this scope yet.
                    </div>
                  )}

                  <div className="procurement-review-actions">
                    <button
                      type="button"
                      className="btn primary"
                      onClick={() => onNavigate?.('procurement-scope-review', responseItem.response_id)}
                    >
                      Open Scope Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </FormSection>
      )}

      <FormSection title="Procurement Tools">
        <div className="procurement-review-note">
          Use the current response-first Procurement Scope Review when you already have a
          `response_id`, need full alias review writeback, or want to inspect raw-line audit
          history directly.
        </div>
        <div className="actions procurement-review-actions">
          <button
            type="button"
            className="btn"
            onClick={() => onNavigate?.('procurement-rfp')}
          >
            Procurement RFP Designer
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => onNavigate?.('procurement-scope-review')}
          >
            Procurement Scope Review
          </button>
        </div>
      </FormSection>
    </div>
  )
}

export default ProcurementReview

